import React, { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ComponentLibrary from './ComponentLibrary';
import FormCanvas from './FormCanvas';
import FormFiller from './FormFiller';
import ToastContainer from './components/ToastContainer';
import { createForm, getForm, updateForm, getConfigurationInformation } from './api/forms';
import toastManager from './utils/toastManager';
import { uploadImage } from './utils/uploadUtils';
import WangEditor from './components/WangEditor';
import { useParams, useNavigate } from 'react-router-dom';
import './designer-layout-fix.css';
import TopNavbar from './components/TopNavbar';
import './components/TopNavbar.css';
import { v4 as uuidv4 } from 'uuid';
import SearchableSelect from './components/SearchableSelect';
// WechatShareDebugger 已移除

const IMAGES_API_BASE_URL = process.env.REACT_APP_images_API_BASE_URL;

function FormDesigner() {
  const { formId } = useParams();
  const navigate = useNavigate();
  
  // 调试：打印获取到的 formId
  console.log('FormDesigner 获取到的 formId:', formId);

  // 设计器相关状态
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [showComponentSettings, setShowComponentSettings] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [formTitle, setFormTitle] = useState('未命名表单');
  const [showTitle, setShowTitle] = useState(true);
  const [formLabel, setFormLabel] = useState('');
  const [formRegion, setFormRegion] = useState('');
  const [formDepartment, setFormDepartment] = useState(''); // 部门
  const [formDesc, setFormDesc] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [showWeChatShare, setShowWeChatShare] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [wxTitle, setWxTitle] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [wxDesc, setWxDesc] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [wxIcon, setWxIcon] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [QRCodeImg, setQRCodeImg] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [formUrl, setFormUrl] = useState('');
  const [previewDesigner, setPreviewDesigner] = useState(false);
  const [rightTab, setRightTab] = useState('component');
  const [formSettingTab, setFormSettingTab] = useState('message');
  const [showWechatModal, setShowWechatModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [wechatShare, setWechatShare] = useState({
    title: '',
    desc: '',
    img: ''
  });
  const [wechatUploading, setWechatUploading] = useState(false);
  const [formMessage, setFormMessage] = useState('提交成功');
  // eslint-disable-next-line no-unused-vars
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formDbId, setFormDbId] = useState('');
  const [configOptions, setConfigOptions] = useState({ plots: [], brandMerchants: [] });
  const [showPreviewData, setShowPreviewData] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [configLoading, setConfigLoading] = useState(false); // 防止重复请求

  // 保证 currentComponent 在函数体内定义
  const currentComponent = selectedComponent ? components.find(comp => comp.id === selectedComponent.id) : null;

  // 加载表单数据
  useEffect(() => {
    let isMounted = true; // 防止组件卸载后的状态更新
    
    console.log('useEffect 触发，formId:', formId);
    async function loadForm() {
      if (formId && formId !== 'new') {
        // 编辑模式，加载表单
        try {
          const res = await getForm(formId);
          let schema = null;
          let wechatShare = null;
          if (res.data && res.data.formSchema) {
            schema = typeof res.data.formSchema === 'string' ? JSON.parse(res.data.formSchema) : res.data.formSchema;
          } else if (res.formSchema) {
            schema = typeof res.formSchema === 'string' ? JSON.parse(res.formSchema) : res.formSchema;
          }
          if (res.data && res.data.wechatShare) {
            wechatShare = typeof res.data.wechatShare === 'string' ? JSON.parse(res.data.wechatShare) : res.data.wechatShare;
          } else if (res.wechatShare) {
            wechatShare = typeof res.wechatShare === 'string' ? JSON.parse(res.wechatShare) : res.wechatShare;
          }
          if (schema) {
            setFormTitle(schema.title || schema.formTitle || '未命名表单');
            setFormLabel(res.data?.label || schema.label || '');
            setFormRegion(res.data?.region || schema.region || '');
            setFormDepartment(res.data?.department || schema.department || ''); // 加载部门
            setFormDesc(schema.formDesc || schema.description || '');
            setComponents(schema.components || []);
            setShowTitle(schema.showTitle !== false);
            setFormMessage(schema.formMessage || '提交成功');
            let ws = res.data && res.data.wechatShare;
            if (typeof ws === 'string') {
              try {
                ws = JSON.parse(ws);
              } catch (e) {
                ws = {};
              }
            }
            setWechatShare(ws || {
              title: '',
              desc: '',
              img: ''
            });
            setFormDbId(res.data?.id || '');
          }
        } catch (e) {
          console.error('表单加载失败:', e);
          if (isMounted) {
            toastManager.error('表单加载失败');
          }
        }
      } else if (formId === 'new') {
        // 新建模式，设置默认组件
        setFormTitle('未命名表单');
        setFormLabel('');
        setFormRegion('');
        setFormDepartment(''); // 重置部门
        setFormDesc('');
        setFormMessage('提交成功');
        
        // 创建默认的五个组件
        const defaultComponents = [
          {
            id: uuidv4(),
            type: 'activity_time',
            fieldMap: '活动时间',
            label: '活动时间',
            hidden: true,
            defaultValue: '',
            props: { placeholder: '请选择活动时间', allowTime: false, dateFormat: 'YYYY/MM/DD' }
          },
          {
            id: uuidv4(),
            type: 'member_brand_merchant',
            fieldMap: '品牌',
            label: '品牌商家',
            hidden: true,
            defaultValue: '',
            props: { options: configOptions.brandMerchants || [], placeholder: '请选择品牌商家' }
          },
          {
            id: uuidv4(),
            type: 'member_community',
            fieldMap: '小区',
            label: '小区',
            hidden: true,
            defaultValue: '',
            props: { options: configOptions.plots || [], placeholder: '请选择小区' }
          },
          {
            id: uuidv4(),
            type: 'shop_address',
            fieldMap: '店铺地址',
            label: '店铺地址',
            hidden: true,
            defaultValue: '',
            props: { options: configOptions.brandMerchants || [], placeholder: '请填写店铺地址' }
          }
        ];
        
        if (isMounted) {
          setComponents(defaultComponents);
        }
      }
    }
    loadForm();
    
    // 清理函数
    return () => {
      isMounted = false;
    };
  }, [formId]);

  // 加载配置项
  useEffect(() => {
    let isMounted = true; // 防止组件卸载后的状态更新
    
    async function fetchConfig() {
      // 防止重复请求
      if (configLoading) {
        console.log('配置信息正在加载中，跳过重复请求');
        return;
      }
      
      try {
        setConfigLoading(true);
        console.log('开始获取配置信息...');
        const res = await getConfigurationInformation();
        
        if (!isMounted) return; // 如果组件已卸载，不更新状态
        
        const newConfigOptions = {
          plots: res.data?.plots || [],
          brandMerchants: res.data?.brandMerchants || []
        };
        setConfigOptions(newConfigOptions);
        
        // 如果是新建模式且已有默认组件，更新它们的选项数据
        if (formId === 'new' && components.length > 0) {
          setComponents(prevComponents => {
            return prevComponents.map(comp => {
              if (comp.type === 'member_community') {
                return {
                  ...comp,
                  props: { ...comp.props, options: newConfigOptions.plots }
                };
              } else if (comp.type === 'member_brand_merchant') {
                return {
                  ...comp,
                  props: { ...comp.props, options: newConfigOptions.brandMerchants }
                };
              } else if (comp.type === 'shop_address') {
                return {
                  ...comp,
                  props: { ...comp.props, options: newConfigOptions.brandMerchants }
                };
              }
              return comp;
            });
          });
        }
              } catch (e) {
          console.error('获取配置信息失败:', e);
          if (isMounted) {
            setConfigOptions({ plots: [], brandMerchants: [] });
          }
        } finally {
          if (isMounted) {
            setConfigLoading(false);
          }
        }
    }
    
    fetchConfig();
    
    // 清理函数
    return () => {
      isMounted = false;
    };
  }, [formId]); // 只依赖formId，避免无限循环

  // 组件操作
  const handleAddComponent = (comp) => {
    const uniqueId = uuidv4(); // 使用uuid生成唯一id
    let newComponent = {
      ...comp,
      id: uniqueId,
      fieldMap: '',
      defaultValue: '',
      props: { ...comp.props } || {}
    };
    // 针对成员组件赋默认属性
    if (comp.type === 'member_name') {
      newComponent.fieldMap = '姓名';
      newComponent.label = '姓名';
      newComponent.props.placeholder = '请输入姓名';
    } else if (comp.type === 'member_phone') {
      newComponent.fieldMap = '电话';
      newComponent.label = '电话';
      newComponent.props.placeholder = '请输入电话';
    } else if (comp.type === 'member_community') {
      newComponent.fieldMap = '小区';
      newComponent.label = '小区';
      newComponent.hidden = true; // 默认隐藏
      newComponent.props.placeholder = '请选择小区';
      newComponent.props.options = configOptions.plots || [];
    } else if (comp.type === 'member_brand_merchant') {
      newComponent.fieldMap = '品牌';
      newComponent.label = '品牌商家';
      newComponent.hidden = true; // 默认隐藏
      newComponent.props.placeholder = '请选择品牌商家';
      newComponent.props.options = configOptions.brandMerchants || [];
    } else if (comp.type === 'shop_address') {
      newComponent.fieldMap = '店铺地址';
      newComponent.label = '店铺地址';
      newComponent.hidden = true; // 默认隐藏
      newComponent.props.placeholder = '请填写店铺地址';
      newComponent.props.options = configOptions.brandMerchants || [];
    } else if (comp.type === 'building_number') {
      newComponent.fieldMap = '楼号';
      newComponent.label = '楼号';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请输入楼号';
    } else if (comp.type === 'house_area') {
      newComponent.fieldMap = '面积';
      newComponent.label = '面积';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请输入面积';
    } else if (comp.type === 'vip_card_number') {
      newComponent.fieldMap = '特权黑卡号';
      newComponent.label = '特权黑卡号';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请输入特权黑卡号';
    } else if (comp.type === 'inspection_community') {
      newComponent.fieldMap = '小区';
      newComponent.label = '小区';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请输入小区';
    } else if (comp.type === 'inspection_datetime') {
      newComponent.fieldMap = '验房时间';
      newComponent.label = '验房时间';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请选择验房时间';
      newComponent.props.allowTime = true; // 允许选择时间
    } else if (comp.type === 'inspection_time') {
      newComponent.fieldMap = '考察时间';
      newComponent.label = '考察时间';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请选择考察时间';
    } else if (comp.type === 'activity_time') {
      newComponent.fieldMap = '活动时间';
      newComponent.label = '活动时间';
      newComponent.hidden = true; // 默认隐藏
      newComponent.props.placeholder = '请选择活动时间';
      newComponent.props.allowTime = false; // 不允许选择时间
    }else if (comp.type === 'inspection_remark') {
      newComponent.fieldMap = '备注';
      newComponent.label = '备注';
      newComponent.hidden = false; // 默认隐藏
      newComponent.props.placeholder = '请填写备注';
      newComponent.props.multiline = true;
    }else if (comp.type === 'activity_requirement') {
      newComponent.fieldMap = '考察需求';
      newComponent.label = '考察需求';
      newComponent.hidden = false; // 默认隐藏
      newComponent.props.placeholder = '请填写考察需求';
    }else if (comp.type === 'shop_address') {
      newComponent.fieldMap = '店铺地址';
      newComponent.label = '店铺地址';
      newComponent.hidden = true; // 默认隐藏
      newComponent.props.placeholder = '请填写店铺地址';
      newComponent.props.options = configOptions.brandMerchants || [];
    } else if (comp.type === 'inspection_brand') {
      newComponent.fieldMap = '品牌';
      newComponent.label = '品牌';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请选择品牌';
      newComponent.props.options = comp.props?.options || [
        '曲美家具',
        '尚品宅配', 
        '索菲亚',
        '红苹果',
        '志邦',
        '欧铂丽',
        '博洛尼',
        '飞美',
        '意风',
        '天坛',
        '阖家乐全屋局改',
        '不需要测量',
        '其他'
      ];
    } else if (comp.type === 'radio' || comp.type === 'checkbox' || comp.type === 'select') {
      // 为单选、多选、下拉选择组件初始化选项
      newComponent.props.options = comp.props?.options || ['选项1', '选项2', '选项3'];
    } else if (comp.type === 'brand_store_cascader') {
      newComponent.fieldMap = '品牌-店铺地址';
      newComponent.label = '品牌-店铺地址';
      newComponent.hidden = false; // 默认显示
      newComponent.props.placeholder = '请选择品牌和店铺地址';
      newComponent.props.mapping = []; // 新增映射关系
      // 使用已加载的品牌商家数据初始化映射
      if (configOptions.brandMerchants && configOptions.brandMerchants.length > 0) {
        newComponent.props.mapping = configOptions.brandMerchants.map(brand => ({
          brand: typeof brand === 'object' ? brand.name : brand,
          store: ''
        }));
      }
    } else if (comp.type === 'activity_brand_multi') {
      newComponent.fieldMap = '品牌';
      newComponent.label = '品牌多选';
      newComponent.hidden = false; // 默认显示
      newComponent.props.options = comp.props?.options || [];
      newComponent.props.placeholder = comp.props?.placeholder || '请选择品牌';
      newComponent.value = '';
    }
    setComponents(prev => [...prev, newComponent]);
  };
  const moveComponent = (from, to) => {
    setComponents(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(from, 1);
      updated.splice(to, 0, removed);
      return updated;
    });
  };
  const handleRemove = (id) => {
    setComponents(prev => prev.filter(c => c.id !== id));
    setSelectedComponent(null);
  };
  const handleUpdateComponent = (id, updates) => {
    setComponents(prev => {
      const updated = prev.map(comp => comp.id === id ? { ...comp, ...updates } : comp);
      // 重新同步 selectedComponent
      const updatedComponent = updated.find(comp => comp.id === id);
      setSelectedComponent(updatedComponent);
      return updated;
    });
  };
  const handleSelectComponent = (component) => {
    setSelectedComponent(component);
  };

  // 保存表单
  const handleSaveForm = async () => {
    if (!formTitle || components.length === 0) {
      toastManager.error('请填写表单标题并添加组件');
      return;
    }
    
    // 自动提取活动时间和活动地址
    let updatedComponents = [...components];

    // 先进行"团购/黑卡派发/黑卡"专用校验：
    // 1) 是否存在必填字段；2) 隐藏字段是否设置默认值
    const labelLowerForCheck = (formLabel || '').toLowerCase();
    const isGroupOrBlack = labelLowerForCheck.includes('团购') || labelLowerForCheck.includes('黑卡') || labelLowerForCheck.includes('黑卡派发');
    if (isGroupOrBlack) {
      const requiredFields = ['活动时间', '小区', '品牌', '店铺地址'];
      const missingFields = [];
      const hiddenNoDefaultRequired = [];

      requiredFields.forEach(req => {
        const comp = updatedComponents.find(c => (c.fieldMap === req) || (!c.fieldMap && (c.label || '').includes(req)));
        if (!comp) {
          missingFields.push(req);
        } else if (comp.hidden && (comp.defaultValue === undefined || comp.defaultValue === null || comp.defaultValue === '')) {
          hiddenNoDefaultRequired.push(req);
        }
      });

      if (missingFields.length > 0 || hiddenNoDefaultRequired.length > 0) {
        let msg = '';
        if (missingFields.length > 0) {
          msg = `表单标签为"${formLabel}"时，必须包含以下字段：活动时间、品牌、小区、店铺地址。缺少：${missingFields.join('、')}`;
        } else if (hiddenNoDefaultRequired.length > 0) {
          msg = `以下字段为隐藏项需设置默认值：${hiddenNoDefaultRequired.join('、')}`;
        }
        toastManager.error(msg);
        return;
      }
    }
    
    // 提取活动时间
    const datePattern = /(\d{1,2}月\d{1,2}日)/;
    const dateMatch = formDesc.match(datePattern);
    if (dateMatch) {
      const dateStr = dateMatch[1];
      const activityTimeComponent = updatedComponents.find(comp => comp.fieldMap === '活动时间');
      if (activityTimeComponent && !activityTimeComponent.defaultValue) {
        // 解析中文日期格式
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        const dateMatch = dateStr.match(/(\d{1,2})月(\d{1,2})日/);
        if (dateMatch) {
          const month = parseInt(dateMatch[1]);
          const day = parseInt(dateMatch[2]);
          
          // 判断年份：如果解析的月份小于当前月份，使用明年
          let targetYear = currentYear;
          if (month < currentMonth) {
            targetYear++;
          }
          
          // 格式化为 YYYY-MM-DD
          const formattedDate = `${targetYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
          
          activityTimeComponent.defaultValue = formattedDate;
        }
      }
    }
    
    // 提取活动地址
    const locationPattern = /活动(地点|地址|地方)：([^<]+)/;
    const locationMatch = formDesc.match(locationPattern);
    if (locationMatch) {
      const eventLocation = locationMatch[2].trim();
      const shopAddressComponent = updatedComponents.find(comp => comp.fieldMap === '店铺地址');
      if (shopAddressComponent && !shopAddressComponent.defaultValue) {
        shopAddressComponent.defaultValue = eventLocation;
      }
      
      // 同时为 brand_store_cascader 组件设置店铺地址
      const brandStoreCascaderComponent = updatedComponents.find(comp => comp.type === 'brand_store_cascader');
      if (brandStoreCascaderComponent && !brandStoreCascaderComponent.defaultValue) {
        // 为级联组件设置默认的店铺地址映射
        const currentMapping = brandStoreCascaderComponent.props?.mapping || [];
        if (currentMapping.length > 0) {
          // 使用第一个品牌的店铺地址
          const firstBrand = currentMapping[0];
          if (firstBrand && typeof firstBrand === 'object') {
            brandStoreCascaderComponent.defaultValue = {
              brand: firstBrand.brand || firstBrand,
              store: eventLocation
            };
          }
        }
      }
    }
    
    // 根据标签文本智能添加必需字段
    if (formLabel && formLabel !== '验房' && formLabel !== '测量') {
      const labelLower = formLabel.toLowerCase();
      const existingFieldMaps = updatedComponents.map(comp => comp.fieldMap).filter(Boolean);
      const existingLabels = updatedComponents.map(comp => comp.label).filter(Boolean);
      const newComponents = [];
      
      // 团购和黑卡/黑卡派发类型必须包含所有字段
      const isRequiredType = labelLower.includes('团购') || labelLower.includes('黑卡') || labelLower.includes('黑卡派发');
      
      if (isRequiredType) {
        // 团购和黑卡派发类型：只进行校验，不自动添加组件
        const missingFields = [];
        const requiredFields = ['小区', '品牌', '店铺地址', '活动时间'];
        
        requiredFields.forEach(requiredField => {
          let fieldExists = false;
          
          // 首先检查字段映射
          if (existingFieldMaps.includes(requiredField)) {
            fieldExists = true;
          } else {
            // 如果没有字段映射，检查标签文本
            if (existingLabels.some(label => label.includes(requiredField))) {
              fieldExists = true;
            }
          }
          
          if (!fieldExists) {
            missingFields.push(requiredField);
          }
        });
        
        if (missingFields.length > 0) {
          toastManager.warning(`"${formLabel}"类型表单缺少必需字段：${missingFields.join('、')}，请手动添加这些字段`);
          // 中止保存流程，仅提示不自动添加
          return;
        }
      } else {
        // 其他类型：按关键词自动添加组件
        // 检查是否包含"小区"
        if (labelLower.includes('小区') && !existingFieldMaps.includes('小区') && !existingLabels.some(label => label.includes('小区'))) {
          newComponents.push({
            id: uuidv4(),
            type: 'member_community',
            fieldMap: '小区',
            label: '小区',
            hidden: false,
            required: true,
            defaultValue: '',
            props: { options: configOptions.plots || [], placeholder: '请选择小区' }
          });
        }
        
        // 检查是否包含"品牌"
        if (labelLower.includes('品牌') && !existingFieldMaps.includes('品牌') && !existingLabels.some(label => label.includes('品牌'))) {
          newComponents.push({
            id: uuidv4(),
            type: 'member_brand_merchant',
            fieldMap: '品牌',
            label: '品牌商家',
            hidden: false,
            required: true,
            defaultValue: '',
            props: { options: configOptions.brandMerchants || [], placeholder: '请选择品牌商家' }
          });
        }
        
        // 检查是否包含"店铺地址"
        if (labelLower.includes('店铺地址') && !existingFieldMaps.includes('店铺地址') && !existingLabels.some(label => label.includes('店铺地址'))) {
          newComponents.push({
            id: uuidv4(),
            type: 'shop_address',
            fieldMap: '店铺地址',
            label: '店铺地址',
            hidden: false,
            required: true,
            defaultValue: '',
            props: { options: configOptions.brandMerchants || [], placeholder: '请填写店铺地址' }
          });
        }
        
        // 检查是否包含"活动时间"
        if (labelLower.includes('活动时间') && !existingFieldMaps.includes('活动时间') && !existingLabels.some(label => label.includes('活动时间'))) {
          newComponents.push({
            id: uuidv4(),
            type: 'activity_time',
            fieldMap: '活动时间',
            label: '活动时间',
            hidden: false,
            required: true,
            defaultValue: '',
            props: { placeholder: '请选择活动时间', allowTime: false, dateFormat: 'YYYY/MM/DD' }
          });
        }
        
        // 如果有新组件需要添加，更新组件列表
        if (newComponents.length > 0) {
          updatedComponents.push(...newComponents);
          setComponents(updatedComponents);
          const addedFields = newComponents.map(comp => comp.label).join('、');
          toastManager.success(`已根据标签"${formLabel}"自动添加字段：${addedFields}`);
        }
      }
    }
    
    // 校验所有隐藏字段必须有默认值，member_phone 类型除外
    const hiddenNoDefault = updatedComponents.filter(comp => comp.hidden && comp.type !== 'member_phone' && (comp.defaultValue === undefined || comp.defaultValue === null || comp.defaultValue === ''));
    if (hiddenNoDefault.length > 0) {
      const names = hiddenNoDefault.map(comp => comp.label || comp.fieldMap || comp.id).join('、');
      toastManager.error(`隐藏字段【${names}】必须设置默认值`);
      return;
    }
    // 校验部门字段必填
    if (!formDepartment) {
      toastManager.error('请选择表单所属部门');
      return;
    }
    // member_phone 类型校验手机号格式
    const phoneFields = updatedComponents.filter(comp => comp.type === 'member_phone' && comp.defaultValue);
    const invalidPhones = phoneFields.filter(comp => !/^1[3-9]\d{9}$/.test(comp.defaultValue));
    if (invalidPhones.length > 0) {
      const names = invalidPhones.map(comp => comp.label || comp.fieldMap || comp.id).join('、');
      toastManager.error(`手机号字段【${names}】默认值格式不正确，需为11位中国大陆手机号`);
      return;
    }
    const formSchema = {
      title: formTitle,
      formTitle: formTitle,
      formDesc: formDesc,
      components: updatedComponents,
      formMessage,
      showTitle,
      region: formRegion,
      createdAt: new Date().toISOString(),
      submissions: []
      // 不要加 wechatShare
    };
    const formData = {
      id: formDbId,
      name: formTitle,
      label: formLabel,
      region: formRegion,
      department: formDepartment, // 部门
      formSchema,
      wechatShare // 只在根字段加
    };
    try {
      if (formId && formId !== 'new') {
        await updateForm(formId, formData);
      } else {
        await createForm(formData);
      }
                  // 保存成功，不显示提示
      navigate('/forms');
    } catch (e) {
      toastManager.error('保存失败');
    }
  };

  // 设计器页面UI
  return (
    <>
      <TopNavbar />
      <ToastContainer />
      <DndProvider backend={HTML5Backend}>
        <div className="designer-bg">
          {/* 组件库头部 - 与顶部操作栏连接 */}
          {/*<div style={{*/}
          {/*  background: 'linear-gradient(135deg, #4f8cff 0%, #3ca1ff 100%)',*/}
          {/*  color: '#fff',*/}
          {/*  height: '72px',*/}
          {/*  padding: '12px 32px',*/}
          {/*  borderBottom: '1px solid #e0e0e0',*/}
          {/*  boxShadow: '0 8px 32px rgba(24,144,255,0.08), 0 1.5px 6px rgba(0,0,0,0.04)',*/}
          {/*  width: '260px',*/}
          {/*  boxSizing: 'border-box',*/}
          {/*  display: 'flex',*/}
          {/*  flexDirection: 'column',*/}
          {/*  justifyContent: 'center',*/}
          {/*  alignItems: 'center',*/}
          {/*  textAlign: 'center',*/}
          {/*  position: 'relative',*/}
          {/*  zIndex: 99,*/}
          {/*  marginLeft: 'auto',*/}
          {/*  marginRight: 'auto',*/}
          {/*  transform: 'translateX(calc(-50vw + 130px + 40px))',*/}
          {/*  borderRadius: '0 0 0 18px'*/}
          {/*}}>*/}
          {/*</div>*/}

          <div className="designer-card">
                      {/* 顶部操作栏 */}
                      <div style={{
                        background: '#fff',
                        borderBottom: '1px solid #e0e0e0',
                        padding: '12px 32px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        boxShadow: '0 2px 8px rgba(24,144,255,0.04)',
                        flexWrap: 'wrap',
                        gap: '6px',
                        width: '100%',
                        boxSizing: 'border-box',
                        minHeight: '48px',
                        maxWidth: '1400px',
                        margin: '0 auto',
                        overflow: 'hidden',
                        borderRadius: '18px 18px 0 0'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', minWidth: '0', flexShrink: 0 }}>
                          <button
                            onClick={() => navigate('/forms')}
                            style={{
                              background: '#f7f9fc',
                              color: '#333',
                              border: '1.5px solid #e0e0e0',
                              borderRadius: '8px',
                              padding: '8px 22px',
                              fontSize: '15px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 8px rgba(24,144,255,0.04)'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#1890ff';
                              e.target.style.borderColor = '#1890ff';
                              e.target.style.color = '#fff';
                              e.target.style.boxShadow = '0 4px 16px rgba(24,144,255,0.10)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#f7f9fc';
                              e.target.style.borderColor = '#e0e0e0';
                              e.target.style.color = '#333';
                              e.target.style.boxShadow = '0 2px 8px rgba(24,144,255,0.04)';
                            }}
                          >
                            ← 返回
                          </button>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', justifyContent: 'flex-end' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#222', lineHeight: '1.2', letterSpacing: '1px' }}>
                              {formId === 'new' ? '新建表单' : '编辑表单'}
                            </div>
                            {formTitle && formTitle !== '未命名表单' && (
                              <div style={{ fontSize: '15px', color: '#333', fontWeight: '600', lineHeight: '1.2' }}>
                                {formTitle}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', minWidth: '0', flexShrink: 1 }}>
                          <button
                            style={{
                              background: '#f7f9fc',
                              color: '#333',
                              border: '1.5px solid #e0e0e0',
                              borderRadius: '8px',
                              padding: '8px 22px',
                              fontSize: '15px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              boxShadow: '0 2px 8px rgba(24,144,255,0.04)'
                            }}
                            onClick={() => {
                              setPreviewData({
                                title: formTitle,
                                desc: formDesc,
                                components,
                                formMessage,
                                showTitle
                              });
                              setShowPreviewData(true);
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#1890ff';
                              e.target.style.borderColor = '#1890ff';
                              e.target.style.color = '#fff';
                              e.target.style.boxShadow = '0 4px 16px rgba(24,144,255,0.10)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#f7f9fc';
                              e.target.style.borderColor = '#e0e0e0';
                              e.target.style.color = '#333';
                              e.target.style.boxShadow = '0 2px 8px rgba(24,144,255,0.04)';
                            }}
                          >
                            预览表单
                          </button>
                          <button
                            style={{
                              background: '#1890ff',
                              color: '#fff',
                              border: '1.5px solid #1890ff',
                              borderRadius: '8px',
                              padding: '8px 22px',
                              fontSize: '15px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              boxShadow: '0 4px 16px rgba(24,144,255,0.10)',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            onClick={handleSaveForm}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#40a9ff';
                              e.target.style.borderColor = '#40a9ff';
                              e.target.style.boxShadow = '0 6px 20px rgba(24,144,255,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#1890ff';
                              e.target.style.borderColor = '#1890ff';
                              e.target.style.boxShadow = '0 4px 16px rgba(24,144,255,0.10)';
                            }}
                          >
                            保存表单
                          </button>
                        </div>
                      </div>
            <div className="designer-layout">
              <div className="component-library">
                <ComponentLibrary onAddComponent={handleAddComponent} />
              </div>
              <div className="form-canvas-wrapper">
                {/* 画布主体 */}
                <FormCanvas
                  components={components}
                  moveComponent={moveComponent}
                  onRemove={handleRemove}
                  onSelectComponent={handleSelectComponent}
                  selectedComponent={selectedComponent}
                  isPreviewMode={isPreviewMode}
                  formTitle={formTitle}
                  formDesc={formDesc}
                  renderFormSettings={() => (
                    <div style={{
                      background: '#f6ffe6',
                      padding: 24,
                      borderRadius: 12,
                      marginBottom: 24,
                      maxWidth: 700,
                      marginLeft: 'auto',
                      marginRight: 'auto',
                      boxSizing: 'border-box',
                      boxShadow: '0 2px 8px rgba(24,144,255,0.04)'
                    }}>
                      {/* 是否显示标题移到右侧"表单设置"，此处移除 */}
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>表单标题</label>
                        <input
                          type="text"
                          value={formTitle}
                          onChange={e => setFormTitle(e.target.value)}
                          style={{
                            width: '100%',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #e0e0e0',
                            fontSize: 16,
                            boxSizing: 'border-box',
                            background: '#fff',
                            marginBottom: 0
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>表单标签 <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>(不对外展示)</span></label>
                        <select
                          value={formLabel}
                          onChange={e => setFormLabel(e.target.value)}
                          style={{
                            width: '100%',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #e0e0e0',
                            fontSize: 16,
                            boxSizing: 'border-box',
                            background: '#fff',
                            marginBottom: 0
                          }}
                        >
                          <option value="">请选择表单标签</option>
                          <option value="测量">测量</option>
                          <option value="验房">验房</option>
                          <option value="黑卡派发">黑卡派发</option>
                          <option value="团购">团购</option>
                          <option value="样板间">样板间</option>
                        </select>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>地区 <span style={{ fontSize: '12px', color: '#999', fontWeight: 'normal' }}>(北京/非北京)</span></label>
                        <select
                          value={formRegion}
                          onChange={e => setFormRegion(e.target.value)}
                          style={{
                            width: '100%',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #e0e0e0',
                            fontSize: 16,
                            boxSizing: 'border-box',
                            background: '#fff',
                            marginBottom: 0
                          }}
                        >
                          <option value="">请选择地区</option>
                          <option value="北京">北京</option>
                          <option value="非北京">非北京</option>
                        </select>
                      </div>
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>表单所属部门 <span style={{ fontSize: '12px', color: '#ff4d4f', fontWeight: 'normal' }}>*</span></label>
                        <select
                          value={formDepartment}
                          onChange={e => setFormDepartment(e.target.value)}
                          style={{
                            width: '100%',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #e0e0e0',
                            fontSize: 16,
                            boxSizing: 'border-box',
                            background: '#fff',
                            marginBottom: 0
                          }}
                        >
                          <option value="">请选择部门</option>
                          <option value="北京运营">北京运营</option>
                          <option value="全国运营">全国运营</option>
                          <option value="成品运营">成品运营</option>
                          <option value="自开发">自开发</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, color: '#666' }}>表单说明</label>
                        <WangEditor value={formDesc} onChange={setFormDesc} style={{ minHeight: 120, maxHeight: 600, overflow: 'hidden' }} />
                      </div>
                    </div>
                  )}
                />
              </div>
              <div className="property-panel">
                <div className="property-tabs" style={{ display: 'flex', borderBottom: '1.5px solid #e0e0e0', marginBottom: 18 }}>
                  <button
                    className={rightTab === 'component' ? 'property-tab active' : 'property-tab'}
                    style={{ flex: 1, padding: '10px 0', border: 'none', background: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer', borderBottom: rightTab === 'component' ? '3px solid #1890ff' : 'none', color: rightTab === 'component' ? '#1890ff' : '#666', transition: 'color 0.2s' }}
                    onClick={() => setRightTab('component')}
                  >组件设置</button>
                  <button
                    className={rightTab === 'form' ? 'property-tab active' : 'property-tab'}
                    style={{ flex: 1, padding: '10px 0', border: 'none', background: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer', borderBottom: rightTab === 'form' ? '3px solid #52c41a' : 'none', color: rightTab === 'form' ? '#52c41a' : '#666', transition: 'color 0.2s' }}
                    onClick={() => setRightTab('form')}
                  >表单设置</button>
                  <button
                    className={rightTab === 'wechat' ? 'property-tab active' : 'property-tab'}
                    style={{ flex: 1, padding: '10px 0', border: 'none', background: 'none', fontWeight: 600, fontSize: 16, cursor: 'pointer', borderBottom: rightTab === 'wechat' ? '3px solid #ff9800' : 'none', color: rightTab === 'wechat' ? '#ff9800' : '#666', transition: 'color 0.2s' }}
                    onClick={() => setRightTab('wechat')}
                  >微信增强</button>
                </div>
                {rightTab === 'component' ? (
                  currentComponent && !isPreviewMode ? (
                    <div className="properties-panel">
                      <h3>属性设置</h3>
                      {/* 字段映射，仅成员组件显示且不可编辑 */}
                      {(currentComponent.type === 'member_name' || currentComponent.type === 'member_phone' || currentComponent.type === 'member_community' || currentComponent.type === 'member_brand_merchant' || currentComponent.type === 'building_number' || currentComponent.type === 'house_area' || currentComponent.type === 'vip_card_number' || currentComponent.type === 'inspection_community' || currentComponent.type === 'inspection_datetime' || currentComponent.type === 'inspection_time' || currentComponent.type === 'activity_time' || currentComponent.type === 'inspection_brand' || currentComponent.type === 'shop_address' || currentComponent.type === 'inspection_remark' || currentComponent.type === 'activity_requirement' || currentComponent.type === 'activity_brand' || currentComponent.type === 'brand_store_cascader' || currentComponent.type === 'activity_brand_multi') && (
                        <div className="property-group">
                          <label>字段映射:</label>
                          <input className="form-input" type="text" value={(currentComponent.type === 'activity_brand' || currentComponent.type === 'member_brand_merchant') ? '品牌' : (currentComponent.fieldMap || '')} disabled style={{ background: '#f5f5f5', color: '#aaa' }} />
                        </div>
                      )}
                      <div className="property-group">
                        <label>标签文本:</label>
                        <input className="form-input" type="text" value={currentComponent.label || ''} onChange={e => handleUpdateComponent(currentComponent.id, { label: e.target.value })} />
                      </div>
                      <div className="property-group">
                        <label>类型:</label>
                        <input className="form-input" type="text" value={currentComponent.type} disabled />
                      </div>
                      <div className="property-group">
                        <label>默认值:</label>
                        <input className="form-input" type="text" value={currentComponent.defaultValue || ''} onChange={e => handleUpdateComponent(currentComponent.id, { defaultValue: e.target.value })} />
                      </div>
                      <div className="property-group" style={{ display: 'flex', gap: 8 }}>
                        <PropertyToggleButton
                          label="必填校验"
                          active={!!currentComponent.required}
                          onClick={() => handleUpdateComponent(currentComponent.id, { required: !currentComponent.required })}
                        />
                        <PropertyToggleButton
                          label="隐藏字段"
                          active={!!currentComponent.hidden}
                          onClick={() => handleUpdateComponent(currentComponent.id, { hidden: !currentComponent.hidden })}
                        />
                      </div>
                      {currentComponent.props && (
                        <div className="property-group">
                          <label>占位符/提示词:</label>
                          <input
                            className="form-input"
                            type="text"
                            value={currentComponent.props.placeholder || ''}
                            onChange={e =>
                              handleUpdateComponent(currentComponent.id, {
                                props: { ...currentComponent.props, placeholder: e.target.value }
                              })
                            }
                          />
                        </div>
                      )}
                      {(currentComponent.type === 'radio' || currentComponent.type === 'checkbox' || currentComponent.type === 'select') && (
                        <>
                          <div className="property-group">
                            <label>显示模式:</label>
                            <select className="form-select" value={currentComponent.props?.layoutType || 'inline'} onChange={e => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, layoutType: e.target.value } })}>
                              <option value="inline">平铺显示</option>
                              <option value="dropdown">下拉选择</option>
                            </select>
                          </div>
                          {currentComponent.type === 'checkbox' && (
                            <div className="property-group">
                              <label>最大选择数量:</label>
                              <input 
                                className="form-input" 
                                type="number" 
                                min="0" 
                                value={currentComponent.props?.maxSelection || 0} 
                                onChange={e => handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, maxSelection: parseInt(e.target.value) || 0 } 
                                })} 
                                placeholder="0表示不限制"
                              />
                            </div>
                          )}
                          <div className="property-group">
                            <label>选项列表:</label>
                            <OptionEditor options={currentComponent.props?.options || ['选项1', '选项2', '选项3']} onChange={options => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, options } })} brandOptions={configOptions.brandMerchants || []} compType={currentComponent.type} />
                          </div>
                        </>
                      )}
                      {/* 属性面板：如果是member_community或member_brand_merchant，选项列表只读显示 */}
                      {(currentComponent.type === 'member_community' || currentComponent.type === 'member_brand_merchant') && (
                        <>
                          <div className="property-group">
                            <label>{currentComponent.type === 'member_community' ? '默认小区' : '默认品牌商家'}:</label>
                            <div className="searchable-select-property-panel">
                              <SearchableSelect
                                value={currentComponent.defaultValue || ''}
                                onChange={(newValue) => handleUpdateComponent(currentComponent.id, { defaultValue: newValue })}
                                options={currentComponent.type === 'member_community' ? configOptions.plots : configOptions.brandMerchants}
                                placeholder={currentComponent.type === 'member_community' ? '请选择默认小区' : '请选择默认品牌商家'}
                                style={{ width: '100%' }}
                              />
                            </div>
                          </div>
                          <div className="property-group">
                            <label>选项数据:</label>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#666', 
                              background: '#f8f9fa', 
                              padding: '8px 12px', 
                              borderRadius: '4px',
                              border: '1px solid #e9ecef'
                            }}>
                              {currentComponent.type === 'member_community' 
                                ? `小区选项: ${configOptions.plots?.length || 0} 个`
                                : `品牌商家选项: ${configOptions.brandMerchants?.length || 0} 个`
                              }
                            </div>
                          </div>
                          <div className="property-group">
                            <button
                              style={{
                                background: '#1890ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                const newOptions = currentComponent.type === 'member_community' 
                                  ? configOptions.plots 
                                  : configOptions.brandMerchants;
                                handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, options: newOptions }
                                });
                              }}
                            >
                              同步最新选项数据
                            </button>
                          </div>

                        </>
                      )}
                      {/* 验房时间组件特殊配置 */}
                      {currentComponent.type === 'inspection_datetime' && (
                        <>
                          <div className="property-group">
                            <label>默认验房时间:</label>
                            <input 
                              className="form-input" 
                              type="datetime-local" 
                              value={currentComponent.defaultValue || ''} 
                              onChange={e => handleUpdateComponent(currentComponent.id, { defaultValue: e.target.value })}
                            />
                          </div>
                          <div className="property-group">
                            <label>时间格式:</label>
                            <select
                              className="form-select"
                              value={currentComponent.props?.dateFormat || 'YYYY-MM-DD HH:mm'}
                              onChange={e => handleUpdateComponent(currentComponent.id, { 
                                props: { ...currentComponent.props, dateFormat: e.target.value }
                              })}
                            >
                              <option value="YYYY-MM-DD HH:mm">年-月-日 时:分</option>
                              <option value="YYYY/MM/DD HH:mm">年/月/日 时:分</option>
                              <option value="MM-DD HH:mm">月-日 时:分</option>
                              <option value="MM/DD HH:mm">月/日 时:分</option>
                            </select>
                          </div>
                          <div className="property-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input 
                                type="checkbox" 
                                checked={currentComponent.props?.allowTime !== false} 
                                onChange={e => handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, allowTime: e.target.checked }
                                })} 
                              />
                              允许选择时间
                            </label>
                          </div>
                        </>
                      )}
                      {/* 考察时间组件特殊配置 */}
                      {currentComponent.type === 'inspection_time' && (
                        <>
                          <div className="property-group">
                            <label>默认考察时间:</label>
                            <input 
                              className="form-input" 
                              type="date" 
                              value={currentComponent.defaultValue || ''}
                              onChange={e => handleUpdateComponent(currentComponent.id, { defaultValue: e.target.value })}
                            />
                          </div>
                          <div className="property-group">
                            <label>时间格式:</label>
                            <select 
                              className="form-select"
                              value={currentComponent.props?.dateFormat || 'YYYY-MM-DD'}
                              onChange={e => handleUpdateComponent(currentComponent.id, { 
                                props: { ...currentComponent.props, dateFormat: e.target.value }
                              })}
                            >
                              <option value="YYYY-MM-DD">年-月-日</option>
                              <option value="YYYY/MM/DD">年/月/日</option>
                              <option value="MM-DD">月-日</option>
                              <option value="MM/DD">月/日</option>
                            </select>
                          </div>
                          <div className="property-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input 
                                type="checkbox" 
                                checked={currentComponent.props?.allowTime || false} 
                                onChange={e => handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, allowTime: e.target.checked }
                                })} 
                              />
                              允许选择时间
                            </label>
                          </div>
                        </>
                      )}
                      {/* 活动时间组件特殊配置 */}
                      {currentComponent.type === 'activity_time' && (
                        <>
                          <div className="property-group">
                            <label>默认活动时间:</label>
                            <input 
                              className="form-input" 
                              type="date" 
                              value={currentComponent.defaultValue || ''} 
                              onChange={e => handleUpdateComponent(currentComponent.id, { defaultValue: e.target.value })}
                            />
                          </div>
                          <div className="property-group">
                            <label>时间格式:</label>
                            <select 
                              className="form-select"
                              value={currentComponent.props?.dateFormat || 'YYYY-MM-DD'}
                              onChange={e => handleUpdateComponent(currentComponent.id, { 
                                props: { ...currentComponent.props, dateFormat: e.target.value }
                              })}
                            >
                              <option value="YYYY-MM-DD">年-月-日</option>
                              <option value="YYYY/MM/DD">年/月/日</option>
                              <option value="MM-DD">月-日</option>
                              <option value="MM/DD">月/日</option>
                            </select>
                          </div>
                        </>
                      )}
                      {/* 验房品牌组件特殊配置 */}
                      {currentComponent.type === 'inspection_brand' && (
                        <>
                          <div className="property-group">
                            <label>最大选择数量:</label>
                            <input 
                              className="form-input" 
                              type="number" 
                              min="0" 
                              value={currentComponent.props?.maxSelection || 0} 
                              onChange={e => handleUpdateComponent(currentComponent.id, { 
                                props: { ...currentComponent.props, maxSelection: parseInt(e.target.value) || 0 } 
                              })} 
                              placeholder="0表示不限制"
                            />
                          </div>
                          <div className="property-group">
                            <label>选项列表:</label>
                            <OptionEditor options={currentComponent.props?.options || []} onChange={options => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, options } })} brandOptions={configOptions.brandMerchants || []} compType={currentComponent.type} />
                          </div>
                          <div className="property-group">
                            <button
                              style={{
                                background: '#1890ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                const newOptions = configOptions.brandMerchants;
                                handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, options: newOptions }
                                });
                              }}
                            >
                              同步最新选项数据
                            </button>
                          </div>

                        </>
                      )}
                      {/* 品牌-店铺级联映射编辑器 */}
                      {currentComponent.type === 'brand_store_cascader' && (
                        <div className="property-group">
                          <BrandStoreMappingEditor
                            mapping={currentComponent.props?.mapping || []}
                            onChange={mapping => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, mapping } })}
                            brandOptions={configOptions.brandMerchants || []}
                          />
                        </div>
                      )}
                      {/* 选项列表编辑和同步，仅品牌单选类型 */}
                      {currentComponent.type === 'activity_brand' && (
                        <>
                          <div className="property-group">
                            <label>选项列表:</label>
                            <OptionEditor options={currentComponent.props?.options || []} onChange={options => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, options } })} brandOptions={configOptions.brandMerchants || []} compType={currentComponent.type} />
                          </div>
                          <div className="property-group">
                            <button
                              style={{
                                background: '#1890ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                const newOptions = configOptions.brandMerchants;
                                handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, options: newOptions }
                                });
                              }}
                            >
                              同步最新选项数据
                            </button>
                          </div>
                        </>
                      )}
                      {/* 品牌多选类型：选项列表和默认值多选 */}
                      {currentComponent.type === 'activity_brand_multi' && (
                        <>
                          <div className="property-group">
                            <label>最大选择数量:</label>
                            <input 
                              className="form-input" 
                              type="number" 
                              min="0" 
                              value={currentComponent.props?.maxSelection || 0} 
                              onChange={e => handleUpdateComponent(currentComponent.id, { 
                                props: { ...currentComponent.props, maxSelection: parseInt(e.target.value) || 0 } 
                              })} 
                              placeholder="0表示不限制"
                            />
                          </div>
                          <div className="property-group">
                            <label>选项列表:</label>
                            <OptionEditor options={currentComponent.props?.options || []} onChange={options => handleUpdateComponent(currentComponent.id, { props: { ...currentComponent.props, options } })} brandOptions={configOptions.brandMerchants || []} compType={currentComponent.type} />
                          </div>
                          <div className="property-group">
                            <label>默认选中品牌（可多选）:</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {(currentComponent.props?.options || []).map((opt, idx) => {
                                const optionValue = typeof opt === 'object' ? opt.name : opt;
                                const checkedArr = (currentComponent.value ? currentComponent.value.split(',') : []);
                                const isChecked = checkedArr.includes(optionValue);
                                return (
                                  <div 
                                    key={optionValue || idx}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '8px 12px',
                                      background: isChecked ? '#1890ff' : '#f8f9fa',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      border: isChecked ? '2px solid #1890ff' : '2px solid transparent'
                                    }}
                                    onClick={() => {
                                      let newArr = checkedArr.slice();
                                      if (isChecked) {
                                        newArr = newArr.filter(v => v !== optionValue);
                                      } else {
                                        // 检查是否达到最大选择数量
                                        const maxSelection = currentComponent.props?.maxSelection || 0;
                                        if (maxSelection === 0 || newArr.length < maxSelection) {
                                          newArr.push(optionValue);
                                        }
                                      }
                                      handleUpdateComponent(currentComponent.id, { value: newArr.join(',') });
                                    }}
                                  >
                                    <span style={{ fontSize: '14px', color: isChecked ? '#fff' : '#333' }}>{optionValue}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="property-group">
                            <button
                              style={{
                                background: '#1890ff',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                const newOptions = configOptions.brandMerchants;
                                handleUpdateComponent(currentComponent.id, { 
                                  props: { ...currentComponent.props, options: newOptions }
                                });
                              }}
                            >
                              同步最新选项数据
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: '#bbb', fontSize: 16, marginTop: 40 }}>请选择左侧组件进行设置</div>
                  )
                ) : rightTab === 'form' ? (
                  <div style={{ padding: 18, color: '#222', fontSize: 15 }}>
                    {/* 二级Tab */}
                    <div style={{ display: 'flex', gap: 0, marginBottom: 18 }}>
                      <button
                        style={{
                          background: formSettingTab === 'message' ? '#eae9e2' : '#f6f5f0',
                          border: '1px solid #d3d1c7',
                          borderRight: 'none',
                          borderRadius: '6px 0 0 6px',
                          padding: '7px 22px',
                          fontWeight: 500,
                          color: '#444',
                          fontSize: 15,
                          cursor: 'pointer',
                          outline: 'none',
                          boxShadow: 'none',
                          transition: 'background 0.2s',
                          zIndex: formSettingTab === 'message' ? 2 : 1
                        }}
                        onClick={() => setFormSettingTab('message')}
                      >提示信息</button>
                      <button
                        style={{
                          background: formSettingTab === 'url' ? '#eae9e2' : '#f6f5f0',
                          border: '1px solid #d3d1c7',
                          borderLeft: 'none',
                          borderRadius: '0 6px 6px 0',
                          padding: '7px 22px',
                          fontWeight: 500,
                          color: '#444',
                          fontSize: 15,
                          cursor: 'pointer',
                          outline: 'none',
                          boxShadow: 'none',
                          transition: 'background 0.2s',
                          zIndex: formSettingTab === 'url' ? 2 : 1
                        }}
                        onClick={() => setFormSettingTab('url')}
                      >跳转到网页</button>
                    </div>
                    {formSettingTab === 'message' ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                          <span style={{ color: '#444' }}>是否显示标题</span>
                          <PropertyToggleButton label="是" active={!!showTitle} onClick={() => setShowTitle(true)} />
                          <PropertyToggleButton label="否" active={!showTitle} onClick={() => setShowTitle(false)} />
                        </div>
                        <div style={{ marginBottom: 8, color: '#888' }}>展示提示文字</div>
                        <textarea style={{ width: '100%', minHeight: 70, borderRadius: 6, border: '1px solid #e0e0e0', padding: 10, fontSize: 15, marginBottom: 12 }} value={formMessage} onChange={e => setFormMessage(e.target.value)} />
                      </>
                    ) : formSettingTab === 'debug' ? (
                      <>
                        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                          调试功能已移除
                        </div>
                      </>
                    ) : (
                      <>
                        <input style={{ width: '100%', borderRadius: 6, border: '1px solid #e0e0e0', padding: 10, fontSize: 15, marginBottom: 12 }} placeholder="请输入网址 http://" />
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ padding: 18, color: '#222', fontSize: 15 }}>
                    <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 17, color: '#222' }}>分享预览</div>
                    <div style={{ background: '#fcfcf7', borderRadius: 14, padding: 18, display: 'flex', alignItems: 'center', marginBottom: 12, boxShadow: '0 2px 12px rgba(24,144,255,0.08), 0 1.5px 6px rgba(0,0,0,0.04)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, color: '#222', letterSpacing: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{wechatShare.title}</div>
                        <div style={{ color: '#a0a0a0', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>{wechatShare.desc}</div>
                      </div>
                      <img src={wechatShare.img.startsWith('/uploads/') ? IMAGES_API_BASE_URL + wechatShare.img : wechatShare.img} alt="" style={{ width: 54, height: 54, borderRadius: 8, marginLeft: 18, objectFit: 'cover', boxShadow: '0 2px 8px rgba(24,144,255,0.10)' }} />
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <button style={{ background: 'none', border: 'none', color: '#1890ff', cursor: 'pointer', fontSize: 15, fontWeight: 600, padding: 0, textDecoration: 'underline', borderRadius: 4, transition: 'color 0.2s' }} onClick={() => setShowWechatModal(true)}>编辑</button>
                    </div>
                    {showWechatModal && (
                      <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ background: '#fff', borderRadius: 8, minWidth: 520, maxWidth: '96vw', padding: '38px 40px 32px 40px', boxShadow: '0 8px 32px rgba(24,144,255,0.13), 0 2px 8px rgba(0,0,0,0.08)', position: 'relative' }}>
                          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 28 }}>微信分享设置</div>
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>标题</div>
                            <input style={{ width: '100%', borderRadius: 4, border: '1px solid #e0e0e0', padding: 8, fontSize: 15 }} value={wechatShare.title} onChange={e => setWechatShare(s => ({ ...s, title: e.target.value }))} />
                          </div>
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>简介</div>
                            <textarea style={{ width: '100%', borderRadius: 4, border: '1px solid #e0e0e0', padding: 8, fontSize: 15, minHeight: 60 }} value={wechatShare.desc} onChange={e => setWechatShare(s => ({ ...s, desc: e.target.value }))} />
                          </div>
                          <div style={{ marginBottom: 18 }}>
                            <div style={{ fontWeight: 500, marginBottom: 6 }}>图标</div>
                            <div style={{ color: '#aaa', fontSize: 13, marginBottom: 8 }}>建议尺寸120px*120px</div>
                            <input type="file" accept="image/*" style={{ marginBottom: 8 }}
                              onChange={async e => {
                                const file = e.target.files && e.target.files[0];
                                if (file) {
                                  setWechatUploading(true);
                                  try {
                                    // 上传到服务器
                                    const result = await uploadImage(file, { showProgress: false, showSuccess: false });
                                    if (result.success && result.url) {
                                      setWechatShare(s => ({ ...s, img: result.url }));
                                    } else {
                                      // 上传失败，使用本地预览
                                      const reader = new FileReader();
                                      reader.onload = ev => setWechatShare(s => ({ ...s, img: ev.target.result }));
                                      reader.readAsDataURL(file);
                                    }
                                  } catch (error) {
                                    console.error('微信分享图片上传失败:', error);
                                  } finally {
                                    setWechatUploading(false);
                                  }
                                }
                              }} />
                            {wechatShare.img && (
                              <div style={{ marginBottom: 8, textAlign: 'center' }}>
                                <img
                                  src={wechatShare.img.startsWith('/uploads/') ? IMAGES_API_BASE_URL + wechatShare.img : wechatShare.img}
                                  alt="微信分享图标"
                                  style={{ width: 60, height: 60, borderRadius: 6, objectFit: 'cover', marginBottom: 8 }}
                                />
                                <div>
                                  <button
                                    type="button"
                                    style={{
                                      background: '#ff4d4f',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: 4,
                                      padding: '4px 12px',
                                      fontSize: 13,
                                      cursor: 'pointer',
                                      marginTop: 4
                                    }}
                                    onClick={() => setWechatShare(s => ({ ...s, img: '' }))}
                                  >
                                    删除图片
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <button style={{ background: '#ff9800', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 32px', fontWeight: 600, fontSize: 16, cursor: wechatUploading ? 'not-allowed' : 'pointer', opacity: wechatUploading ? 0.6 : 1 }} onClick={() => setShowWechatModal(false)} disabled={wechatUploading}>确定</button>
                          </div>
                          <button style={{ position: 'absolute', right: 18, top: 18, background: 'none', border: 'none', fontSize: 22, color: '#aaa', cursor: 'pointer' }} onClick={() => setShowWechatModal(false)} title="关闭">×</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DndProvider>
      {previewDesigner && (
        <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.35)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <button style={{ position: 'fixed', right: 36, top: 36, background: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', fontSize: 28, cursor: 'pointer', zIndex: 10001, color: '#888', transition: 'background 0.2s' }} onClick={() => setPreviewDesigner(false)} title="关闭预览">×</button>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 400, maxWidth: 600, maxHeight: '80vh', overflow: 'auto', position: 'relative' }}>
            {formDesc && (
              <div style={{ background: '#f6ffe6', borderRadius: 8, padding: 16, marginBottom: 24 }}>
                <div style={{ color: '#333', fontWeight: 600, fontSize: 16, marginBottom: 8 }}>表单说明</div>
                <div style={{ fontSize: 14, color: '#444' }} dangerouslySetInnerHTML={{ __html: formDesc }} />
              </div>
            )}
            <FormFiller formId={null} formData={{ ...{ title: formTitle, components, showTitle }, formDesc: formDesc }} previewMode />
          </div>
        </div>
      )}
      {/* 预览数据弹窗 */}
      {showPreviewData && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 32,
            minWidth: 320,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            position: 'relative'
          }}>
            <button onClick={() => setShowPreviewData(false)} style={{
              position: 'absolute',
              right: 18,
              top: 18,
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#222'
            }}>×</button>
            <h2 style={{ marginBottom: 16, fontWeight: 700, fontSize: 22 }}>表单预览</h2>
            <FormFiller formId={null} formData={previewData} previewMode={true} />
          </div>
        </div>
      )}
    </>
  );
}

// 选项编辑器组件
function OptionEditor({ options, onChange, brandOptions, compType }) {
  // 防御性处理，确保 options 和 brandOptions 一定为数组
  options = Array.isArray(options) ? options : [];
  brandOptions = Array.isArray(brandOptions) ? brandOptions : [];
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');

  const handleChange = (i, value) => {
    const newOpts = options.slice();
    newOpts[i] = value;
    onChange(newOpts);
  };
  const handleAdd = () => onChange([...options, '新选项']);
  const handleDel = (i) => onChange(options.filter((_, idx) => idx !== i));

  // 品牌库多选弹窗相关
  const handleBrandSelect = (brand, checked) => {
    const brandName = typeof brand === 'object' ? brand.name : brand;
    if (checked) {
      setSelectedBrands(prev => [...prev, brandName]);
    } else {
      setSelectedBrands(prev => prev.filter(b => b !== brandName));
    }
  };
  const handleBrandSync = () => {
    onChange(selectedBrands);
    setShowBrandModal(false);
  };
  // 搜索过滤
  const filteredBrands = brandOptions.filter(brand => {
    const name = typeof brand === 'object' ? brand.name : brand;
    return name.toLowerCase().includes(brandSearch.trim().toLowerCase());
  });

  return (
    <div className="option-editor">
      {options.map((opt, i) => (
        <div key={i} className="option-item" style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <input
            value={typeof opt === 'object' ? (opt.name || '') : opt}
            onChange={e => handleChange(i, e.target.value)}
            placeholder="请输入选项内容"
            className="form-input"
            style={{ flex: 1, marginRight: 8, borderRadius: 8, padding: '10px 14px', border: '1.5px solid #d9d9d9', background: '#fafcff', fontSize: '1rem', boxSizing: 'border-box' }}
          />
          <button
            type="button"
            onClick={() => handleDel(i)}
            className="option-delete-btn"
            title="删除选项"
            style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #d9d9d9', background: '#fff', color: '#ff7875', fontWeight: 500, cursor: 'pointer', fontSize: 15, marginLeft: 0 }}
          >删除</button>
        </div>
      ))}
      <button
        type="button"
        onClick={handleAdd}
        className="option-add-btn"
        style={{ padding: '7px 18px', borderRadius: 6, border: '1.5px solid #3182ce', background: '#f5faff', color: '#3182ce', fontWeight: 600, cursor: 'pointer', fontSize: 15, marginTop: 4 }}
      >+ 添加选项</button>
      {/* 仅品牌组件显示品牌库选择按钮 */}
      {(compType === 'inspection_brand' || compType === 'activity_brand' || compType === 'activity_brand_multi') && brandOptions.length > 0 && (
        <button
          type="button"
          onClick={() => { setShowBrandModal(true); setSelectedBrands(options.map(opt => typeof opt === 'object' ? opt.name : opt)); }}
          style={{ marginLeft: 12, padding: '7px 18px', borderRadius: 6, border: '1.5px solid #52c41a', background: '#f6ffed', color: '#52c41a', fontWeight: 600, cursor: 'pointer', fontSize: 15 }}
        >从品牌库选择</button>
      )}
      {/* 品牌库多选弹窗 */}
      {showBrandModal && (
        <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, minWidth: 320, maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', position: 'relative' }}>
            <button onClick={() => setShowBrandModal(false)} style={{ position: 'absolute', right: 18, top: 18, background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#222' }}>×</button>
            <h3 style={{ marginBottom: 16, fontWeight: 700, fontSize: 18 }}>从品牌库选择品牌</h3>
            <input
              type="text"
              value={brandSearch}
              onChange={e => setBrandSearch(e.target.value)}
              placeholder="搜索品牌名称"
              style={{ width: '100%', marginBottom: 16, padding: '8px 12px', borderRadius: 6, border: '1.5px solid #e0e0e0', fontSize: 15 }}
            />
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 18 }}>
              {filteredBrands.map((brand, idx) => (
                <label key={typeof brand === 'object' ? brand.id || brand.name : idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 8, cursor: 'pointer', fontSize: 15 }}>
                  <input
                    type="checkbox"
                    checked={selectedBrands.includes(typeof brand === 'object' ? brand.name : brand)}
                    onChange={e => handleBrandSelect(brand, e.target.checked)}
                    style={{ marginRight: 8, width: 16, height: 16, accentColor: undefined, boxShadow: 'none', border: '1px solid #ccc', borderRadius: 2, appearance: 'checkbox' }}
                  />
                  {typeof brand === 'object' ? brand.name : brand}
                </label>
              ))}
            </div>
            <button
              onClick={handleBrandSync}
              style={{ padding: '8px 24px', borderRadius: 6, border: 'none', background: '#1890ff', color: '#fff', fontWeight: 600, fontSize: 16, cursor: 'pointer' }}
            >确定</button>
          </div>
        </div>
      )}
    </div>
  );
}

// 品牌-店铺级联编辑器
function BrandStoreMappingEditor({ mapping, onChange, brandOptions }) {
  // mapping: [{ brand: '品牌A', store: '店铺地址' }, ...]
  const [localMapping, setLocalMapping] = React.useState(mapping || []);
  const [newBrand, setNewBrand] = React.useState('');
  const [brandSearch, setBrandSearch] = React.useState('');

  React.useEffect(() => { setLocalMapping(mapping || []); }, [mapping]);

  // 添加品牌
  const handleAddBrand = () => {
    if (!newBrand.trim() || localMapping.some(item => item.brand === newBrand.trim())) return;
    const updated = [...localMapping, { brand: newBrand.trim(), store: '' }];
    setLocalMapping(updated);
    onChange(updated);
    setNewBrand('');
  };
  // 删除品牌
  const handleDelBrand = (idx) => {
    const updated = localMapping.filter((_, i) => i !== idx);
    setLocalMapping(updated);
    onChange(updated);
  };
  // 修改店铺
  const handleStoreChange = (idx, value) => {
    const updated = localMapping.slice();
    updated[idx].store = value;
    setLocalMapping(updated);
    onChange(updated);
  };
  // 品牌库搜索过滤
  const filteredBrands = (brandOptions || []).filter(b => {
    const name = typeof b === 'object' ? b.name : b;
    return name.toLowerCase().includes(brandSearch.trim().toLowerCase());
  });

  return (
    <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 12, border: '1.5px solid #e0e0e0', marginBottom: 8 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>品牌-店铺地址映射</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          value={newBrand}
          onChange={e => setNewBrand(e.target.value)}
          placeholder="输入品牌名称或从下方选择"
          style={{ flex: 1, borderRadius: 6, border: '1.5px solid #e0e0e0', padding: '7px 12px', fontSize: 15 }}
        />
        <button onClick={handleAddBrand} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#1890ff', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>添加品牌</button>
      </div>
      <input
        value={brandSearch}
        onChange={e => setBrandSearch(e.target.value)}
        placeholder="品牌库搜索"
        style={{ width: '100%', borderRadius: 6, border: '1.5px solid #e0e0e0', padding: '7px 12px', fontSize: 15, marginBottom: 8 }}
      />
      <div style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 12 }}>
        {filteredBrands.map((b, idx) => {
          const name = typeof b === 'object' ? b.name : b;
          return (
            <span key={name} style={{ display: 'inline-block', margin: '0 8px 8px 0', padding: '4px 10px', borderRadius: 6, background: '#f0f7ff', color: '#1890ff', fontSize: 14, cursor: 'pointer', border: '1px solid #91d5ff' }}
              onClick={() => setNewBrand(name)}>{name}</span>
          );
        })}
      </div>
      {localMapping.map((item, idx) => (
        <div key={item.brand} style={{ background: '#fff', borderRadius: 6, padding: 10, marginBottom: 10, border: '1px solid #e0e0e0' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 15, color: '#333', marginRight: 8 }}>{item.brand}</span>
            <button onClick={() => handleDelBrand(idx)} style={{ marginLeft: 8, color: '#ff7875', background: 'none', border: 'none', fontSize: 15, cursor: 'pointer' }}>删除品牌</button>
          </div>
          <div style={{ marginLeft: 12 }}>
            <input
              value={item.store}
              onChange={e => handleStoreChange(idx, e.target.value)}
              placeholder="输入店铺地址"
              style={{ borderRadius: 6, border: '1.5px solid #e0e0e0', padding: '6px 10px', fontSize: 14, flex: 1, marginRight: 8 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// 属性面板专用切换按钮组件
function PropertyToggleButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 18px',
        borderRadius: 6,
        border: active ? '1.5px solid #1890ff' : '1.5px solid #e0e0e0',
        background: active ? '#1890ff' : '#f7f9fc',
        color: active ? '#fff' : '#333',
        fontWeight: 600,
        fontSize: 15,
        cursor: 'pointer',
        marginRight: 12,
        marginBottom: 6,
        transition: 'all 0.2s'
      }}
    >
      {label}
    </button>
  );
}

export default FormDesigner; 